import { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { 
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import { useUpdateConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ForwardButton = styled(Button)`
  background-color: #4caf50;
`;

const ReturnButton = styled(Button)`
  background-color: #ff9800;
`;

const RejectButton = styled(Button)`
  background-color: #f44336;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
  margin-top: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const SelectedAssetsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedAssetsTitle = styled.h3`
  margin-bottom: 10px;
  color: #1976d2;
`;

const SummaryContainer = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  padding: 5px 10px;
  border-radius: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const SummaryItemName = styled.span`
  font-weight: 500;
  margin-right: 10px;
`;

const SummaryItemCount = styled.span`
  background-color: #1976d2;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.8rem;
`;

const UnitHeadInboxFormBTS = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');
  
  // Auth and office data
  const auth = useAuth();
  const user_current_office = useSelector((state) => state.office.off);
  
  // API queries
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });
  
  const { storeoffices } = useGetStoreOfficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });
  
  // Mutations
  const [updateConsumableReq, { isLoading: isUpdatingReq }] = useUpdateConsumableReqMutation();  

  // Memoized values to prevent unnecessary recalculations
  const storeOffice = useMemo(() => 
    Object.values(storeoffices || {})?.find(x => x.storeoffice === row?.req_store) || {},
    [storeoffices, row?.req_store]
  );
  
  const storeIncharge_id = useMemo(() => 
    storeOffice?.roleUsers?.L1ReqApprover_C || "",
    [storeOffice]
  );
  
  const BO_id = useMemo(() => 
    officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.Bo : "",
    [officedata, user_current_office]
  );

  // Memoized sender and receiver objects for request updates
  const senderObject = useMemo(() => ({
    id: auth?.id, 
    name: getSingleuser?.[auth?.id]?.name, 
    office: user_current_office, 
    designation: auth?.designation?.[user_current_office] 
  }), [auth, getSingleuser, user_current_office]);

  const storeReceiverObject = useMemo(() => ({
    id: storeIncharge_id, 
    name: getSingleuser?.[storeIncharge_id]?.name, 
    office: row?.req_store, 
    designation: getSingleuser?.[storeIncharge_id]?.designation?.[row?.req_store] 
  }), [storeIncharge_id, getSingleuser, row?.req_store]);

  const boReceiverObject = useMemo(() => ({
    id: BO_id, 
    name: getSingleuser?.[BO_id]?.name, 
    office: user_current_office, 
    designation: getSingleuser?.[BO_id]?.designation?.[user_current_office] 
  }), [BO_id, getSingleuser, user_current_office]);

  const storeReceiverString = useMemo(() => 
    `${nameStringFn(getSingleuser?.[storeIncharge_id])}-${row?.req_store}-${getSingleuser?.[storeIncharge_id]?.designation?.[row?.req_store] || 'Unknown'}`,
    [getSingleuser, storeIncharge_id, row?.req_store]
  );

  const boReceiverString = useMemo(() => 
    `${nameStringFn(getSingleuser?.[BO_id])}-${user_current_office}-${getSingleuser?.[BO_id]?.designation?.[user_current_office] || 'Unknown'}`,
    [getSingleuser, BO_id, user_current_office]
  );

  // Get requested consumables from the latest request
  const requestedConsumables = useMemo(() => {
    if (!row?.consumables_requested || row.consumables_requested.length === 0) {
      return [];
    }
    const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
    return latestRequest?.data || [];
  }, [row?.consumables_requested]);

  // Generate summary data from consumables
  const generateSummary = useCallback((items) => {
    if (!items || items.length === 0) return [];

    return Object.entries(
      items.reduce((acc, item) => {
        const { consumable } = item;
        acc[consumable] = (acc[consumable] || 0) + parseInt(item.quantity || 0, 10);
        return acc;
      }, {})
    ).map(([name, count]) => ({ name, count }));
  }, []);

  const consumablesSummary = useMemo(() => 
    generateSummary(requestedConsumables),
    [generateSummary, requestedConsumables]
  );

  // Validate remark function
  const validateRemark = useCallback(() => {
    if (!remark.trim()) {
      return false;
    }
    return true;
  }, [remark]);

  // Handle change for remark
  const handleRemarkChange = useCallback((e) => {
    setRemark(e.target.value);
  }, []);

  // Create request update function to avoid repeating code
  const updateRequest = useCallback(async (senttoData) => {
    try {
      const body = { id: row?._id, senttoData };
      await updateConsumableReq(body).unwrap();
      return true;
    } catch (error) {
      console.error("Error updating request:", error);
      return false;
    }
  }, [row?._id, updateConsumableReq]);

  // Handle forwarding to store
  const handleForwardToStore = useCallback(async () => {
    if (!validateRemark()) {
      alert("Please provide a remark before forwarding.");
      return;
    }
    
    if (!storeIncharge_id) {
      alert("Store incharge information not found.");
      return;
    }
    
    const senttoData = {
      sent_to_tag: "uhreq_bts_forward_store",
      Text: remark,
      sent_by: senderObject,
      sent_to: storeReceiverObject,
      action: "Request forwarded to store",
      curr_sent_to_string: storeReceiverString,
      time_stamp: new Date()
    };
    
    const success = await updateRequest(senttoData);
    if (success) {
      alert("Request forwarded to store successfully.");
      handleBack();
    } else {
      alert("There was an error forwarding the request. Please try again.");
    }
  }, [
    validateRemark, 
    storeIncharge_id, 
    remark, 
    senderObject, 
    storeReceiverObject, 
    storeReceiverString, 
    updateRequest, 
    handleBack
  ]);
  
  // Handle returning to BO for modification
  const handleReturnForModification = useCallback(async () => {
    if (!validateRemark()) {
      alert("Please provide a remark explaining what needs to be modified.");
      return;
    }
    
    const senttoData = {
      sent_to_tag: "boreq_bts_rtrned_uhlvl",
      Text: remark,
      sent_by: senderObject,
      sent_to: boReceiverObject,
      action: "Request returned to BO for modification",
      curr_sent_to_string: boReceiverString,
      time_stamp: new Date()
    };
    
    const success = await updateRequest(senttoData);
    if (success) {
      alert("Request returned to BO for modification.");
      handleBack();
    } else {
      alert("There was an error returning the request. Please try again.");
    }
  }, [
    validateRemark, 
    remark, 
    senderObject, 
    boReceiverObject, 
    boReceiverString, 
    updateRequest, 
    handleBack
  ]);
  
  // Handle rejecting the request
  const handleRejectRequest = useCallback(async () => {
    if (!validateRemark()) {
      alert("Please provide a remark explaining the reason for rejection.");
      return;
    }
    
    const senttoData = {
      sent_to_tag: "boreq_bts_rejected",
      Text: remark,
      sent_by: senderObject,
      sent_to: boReceiverObject,
      action: "Request rejected by Unit Head",
      curr_sent_to_string: boReceiverString,
      curr_status: "Rejected",
      time_stamp: new Date()
    };
    
    const success = await updateRequest(senttoData);
    if (success) {
      alert("Request has been rejected.");
      handleBack();
    } else {
      alert("There was an error rejecting the request. Please try again.");
    }
  }, [
    validateRemark, 
    remark, 
    senderObject, 
    boReceiverObject, 
    boReceiverString, 
    updateRequest, 
    handleBack
  ]);

  return (
    <>
      {/* Display Requested Consumables in Table */}
      {requestedConsumables.length > 0 && (
        <SelectedAssetsContainer>
          <SelectedAssetsTitle>Consumables Requested for Issue</SelectedAssetsTitle>

          {/* Summary section */}
          <SummaryContainer>
            <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
              Total: {requestedConsumables.reduce((sum, item) => sum + parseInt(item.quantity || 0, 10), 0)} items
            </span>
            {consumablesSummary.map((item, index) => (
              <SummaryItem key={index}>
                <SummaryItemName>{item.name}</SummaryItemName>
                <SummaryItemCount>{item.count}</SummaryItemCount>
              </SummaryItem>
            ))}
          </SummaryContainer>
          
          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell>Sl.No.</TableCell>
                  <TableCell>Consumable</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Part No</TableCell>
                  <TableCell>Requested Qty</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {requestedConsumables.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item?.consumable || '-'}</TableCell>
                    <TableCell>{item?.asset || 'Not Applicable'}</TableCell>
                    <TableCell>{item?.assettag?.label || 'Not Applicable'}</TableCell>
                    <TableCell>{item?.partno || '-'}</TableCell>
                    <TableCell>{item?.quantity || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </SelectedAssetsContainer>
      )}
      
      <RemarkContainer>
        <TextField
          fullWidth
          label="Remarks"
          variant="outlined"
          multiline
          rows={4}
          value={remark}
          onChange={handleRemarkChange}
          placeholder="Add your remarks here..."
        />
      </RemarkContainer>
      
      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        <ForwardButton 
          onClick={handleForwardToStore}
          disabled={isUpdatingReq}
        >
          Forward to Store
        </ForwardButton>
        
        <ReturnButton 
          onClick={handleReturnForModification}
          disabled={isUpdatingReq}
        >
          Return to BO for Modification
        </ReturnButton>
        
        <RejectButton 
          onClick={handleRejectRequest}
          disabled={isUpdatingReq}
        >
          Reject Request
        </RejectButton>
      </div>
    </>
  );
};

export default UnitHeadInboxFormBTS;